import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  createFolder,
  createPickerSession,
  deleteFolder,
  deleteItems,
  getLibraryImportJob,
  getPickerSession,
  googleAuthorize,
  googleStatus,
  listActiveImports,
  listFolders,
  listImportHistory,
  listLibrary,
  moveItems,
  revokeGoogle,
  startLibraryImport,
  updateFolder,
  SCOPE_ALERT,
  type FolderDisposition,
  type LibraryFolder,
  type LibraryImportJob,
  type LibraryItem,
  type LibraryScope,
} from "@/api/library";
import {
  durationLabel,
  focalPoint,
  searchLibrary,
  tileUrl,
  type MediaSearchItem,
} from "@/api/memories";
import {
  classifyArchivePick,
  uploadArchive,
  zipMediaFiles,
} from "@/api/archive";
import { SpeakerGlyph } from "@/components/memories";
import { ComposeMomentOverlay } from "@/components/ComposeMomentOverlay";
import { extractApiError, httpStatus } from "@/api/client";
import { subscriptionQuery } from "@/lib/queries";
import { IS_IOS, IS_MAC } from "@/lib/device";
import { collectDroppedFiles } from "@/lib/dropFiles";
import { countLabel } from "@/lib/format";
import { FocalImage } from "@/components/FocalImage";
import { Lightbox } from "@/components/lightbox";
import { TailSentinel } from "@/components/TailSentinel";
import { ConfirmDialog, Modal } from "@/components/Modal";
import {
  Card,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  RadioRow,
  SectionLabel,
} from "@/components/ui";
import {
  IconCircle,
  IconFacebook,
  IconGooglePhotos,
  IconApple,
  IconImports,
  IconSearch,
} from "@/components/icons";

/**
 * Library — the holding place for media brought into Memily but not
 * yet attached to a Moment or Story (libraryBuild.md, D-LIB.1–8).
 *
 * Folders carry user/family scope; media inherits the folder it's
 * dropped into, with the D-LIB.2 visibility alert on scope-crossing
 * moves. Family-scoped tiles and folders wear the small family
 * watermark (D-LIB.3). Folder delete asks what happens to the
 * contents (D-LIB.7). The Google picker import lands everything in
 * the Library (D-LIB.8) with job progress polled like data-export.
 */

const LibraryPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  const { folder } = Route.useSearch();
  return (
    <AppShell user={user}>
      <LibraryView folderId={folder ?? null} />
    </AppShell>
  );
};

const LibraryView = ({ folderId }: { folderId: string | null }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const folders = useQuery({
    queryKey: ["library-folders"] as const,
    queryFn: listFolders,
    staleTime: 30_000,
  });

  const available = folders.data !== null && !folders.isError;
  const currentFolder =
    (folderId && folders.data?.find((f) => f.folder_id === folderId)) ||
    null;

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["library-folders"] });
    void queryClient.invalidateQueries({ queryKey: ["library-items"] });
    void queryClient.invalidateQueries({ queryKey: ["subscription"] });
  }, [queryClient]);

  if (folders.isPending) {
    return <LoadingBlock label="Opening your library…" />;
  }

  return (
    <div className="mx-auto max-w-4xl">
      {/* Small: title, then the import button centered beneath.
          Large: one row — title left, button right. */}
      <header className="flex flex-col items-center gap-5 text-center md:flex-row md:items-center md:justify-between md:gap-4 md:text-left">
        <div>
          {currentFolder ? (
            <nav aria-label="Breadcrumb" className="mb-1">
              <Link
                to="/library"
                className="font-mono text-[12px] uppercase tracking-[0.18em] text-taupe-deep hover:text-ink"
              >
                Library
              </Link>
            </nav>
          ) : null}
          <h1
            className="flex items-center gap-3 font-serif text-[28px] leading-[34px] text-ink"
            style={{ fontWeight: 600 }}
          >
            {currentFolder?.name ?? "Library"}
            {currentFolder?.scope === "family" && (
              <FamilyMark size={20} label="Shared with your family" />
            )}
          </h1>
          <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
            {currentFolder
              ? currentFolder.scope === "family"
                ? "Everyone in your circle can see what's here."
                : "Only you can see what's here."
              : "A quiet place to hold photos, videos, and audio before they become memories."}
          </p>
        </div>
        {available && <ImportButton refresh={refresh} />}
      </header>

      {!available ? (
        <div className="mt-8">
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-paper text-ink">
                <IconGooglePhotos className="h-5 w-5" />
              </span>
              <div>
                <p className="font-sans text-[16px] font-medium text-ink">
                  Bring in from Google Photos
                </p>
                <p className="mt-0.5 font-sans text-[13px] leading-[20px] text-ink-soft">
                  Years of family photos, brought home at full quality —
                  held here until you're ready to share them.
                </p>
                <p className="mt-3 font-mono text-[12px] uppercase tracking-[0.18em] text-taupe-deep">
                  Coming soon
                </p>
              </div>
            </div>
          </Card>
          <p className="mt-4 font-serif text-[13px] italic text-ink-soft">
            We're putting the finishing touches on this. Check back here.
          </p>
        </div>
      ) : (
        <LibraryBrowser
          folders={folders.data ?? []}
          folderId={folderId}
          currentFolder={currentFolder}
          refresh={refresh}
          onOpenFolder={(id) =>
            void navigate({
              to: "/library",
              search: id ? { folder: id } : {},
            })
          }
        />
      )}
    </div>
  );
};

// ── The family watermark (D-LIB.3) ─────────────────────────────────

const FamilyMark = ({
  size = 14,
  label,
  chip = false,
}: {
  size?: number;
  label: string;
  chip?: boolean;
}) => (
  <span
    role="img"
    aria-label={label}
    title={label}
    className={
      chip
        ? "absolute bottom-1.5 left-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/45 text-white backdrop-blur-[2px]"
        : "inline-flex text-taupe-deep"
    }
  >
    <IconCircle style={{ width: size, height: size }} />
  </span>
);

// ── Browser: folders + grid + selection + DnD ──────────────────────

const LibraryBrowser = ({
  folders,
  folderId,
  currentFolder,
  refresh,
  onOpenFolder,
}: {
  folders: LibraryFolder[];
  folderId: string | null;
  currentFolder: LibraryFolder | null;
  refresh: () => void;
  onOpenFolder: (id: string | null) => void;
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [managingFolder, setManagingFolder] = useState<LibraryFolder | null>(
    null,
  );
  const [pendingMove, setPendingMove] = useState<{
    ids: string[];
    target: LibraryFolder | null;
    alert: string | null;
  } | null>(null);
  const [moving, setMoving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dragIds = useRef<string[]>([]);
  // Google-Photos-style selection mechanics (Dustin 2026-08-14):
  // shift-click selects the range from the last-touched tile, and
  // holding the mouse down on a checkbox then sweeping across tiles
  // selects them. Anchor + click-suppression live in refs — they
  // must not re-render the grid.
  const lastIndexRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const [dragSelecting, setDragSelecting] = useState(false);
  // Shift-hover range preview (Dustin 2026-08-14, the Google web
  // behavior): once something is selected, holding Shift makes the
  // cursor paint a live preview of the prospective range; the
  // shift-click commits it. Hover rides in a ref at all times (zero
  // re-renders) and is promoted to state only while Shift is held.
  const hoverRef = useRef<number | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setShiftHeld(true);
        setHoverIndex(hoverRef.current);
      }
    };
    const clear = (e: KeyboardEvent | FocusEvent) => {
      if ("key" in e && e.key !== "Shift") return;
      setShiftHeld(false);
      setHoverIndex(null);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", clear);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", clear);
      window.removeEventListener("blur", clear);
    };
  }, []);

  const previewRange =
    shiftHeld &&
    hoverIndex !== null &&
    lastIndexRef.current !== null &&
    selected.size > 0
      ? ([
          Math.min(lastIndexRef.current, hoverIndex),
          Math.max(lastIndexRef.current, hoverIndex),
        ] as const)
      : null;

  const items = useInfiniteQuery({
    queryKey: ["library-items", folderId ?? "root"] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listLibrary(folderId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
    // Imports land before their thumbnails finish processing; keep
    // polling while a tile is genuinely mid-pipeline so the grid
    // fills in on its own. Two hard lessons from the first big
    // import (UAT 2026-08-13): `failed` is TERMINAL — treating any
    // non-processed state as "still working" polled forever — and a
    // deep-scrolled infinite query refetches EVERY loaded page per
    // tick, hammering the API into 429s. Fresh arrivals sort
    // newest-first, so page 1 is the only live window worth watching.
    refetchInterval: (query) => {
      const firstPage = query.state.data?.pages[0]?.items ?? [];
      const processing = firstPage.some(
        (i) =>
          i.media.display_state === "uploaded" ||
          i.media.display_state === "processing",
      );
      return processing ? 8_000 : false;
    },
  });
  const list = useMemo(
    () => items.data?.pages.flatMap((p) => p.items) ?? [],
    [items.data],
  );
  // The lightbox only carries processed items — a tile mid-pipeline
  // can't be opened, and prev/next must not land on a blank slide.
  const viewable = useMemo(
    () => list.filter((i) => i.media.display_state === "processed"),
    [list],
  );
  // The compose dialog needs the full items (asset ids + media for
  // cover thumbs), not just the id set the checkboxes track.
  const selectedItems = useMemo(
    () => list.filter((i) => selected.has(i.library_item_id)),
    [list, selected],
  );

  useEffect(() => setSelected(new Set()), [folderId]);

  const clearSelection = () => setSelected(new Set());

  // Scope-crossing check → D-LIB.2 alert before the move commits.
  const requestMove = (ids: string[], target: LibraryFolder | null) => {
    const movingItems = list.filter((i) =>
      ids.includes(i.library_item_id),
    );
    const targetScope: LibraryScope = target?.scope ?? "user";
    const crossesToFamily =
      targetScope === "family" &&
      movingItems.some((i) => i.scope === "user");
    const crossesToUser =
      targetScope === "user" &&
      target !== null &&
      movingItems.some((i) => i.scope === "family");
    const alert = crossesToFamily
      ? SCOPE_ALERT.toFamily
      : crossesToUser
        ? SCOPE_ALERT.toUser
        : null;
    if (alert) {
      setPendingMove({ ids, target, alert });
    } else {
      commitMove(ids, target);
    }
  };

  const commitMove = (ids: string[], target: LibraryFolder | null) => {
    setMoving(true);
    moveItems(ids, target?.folder_id ?? null)
      .then(() => {
        setPendingMove(null);
        setError(null);
        clearSelection();
        refresh();
      })
      .catch((err) =>
        setError(
          extractApiError(err)?.message ??
            "Couldn't move those right now. Please try again.",
        ),
      )
      .finally(() => setMoving(false));
  };

  const remove = useMutation({
    mutationFn: () => deleteItems([...selected]),
    onSuccess: () => {
      setConfirmingDelete(false);
      clearSelection();
      setError(null);
      refresh();
    },
    onError: (err) => {
      setConfirmingDelete(false);
      setError(
        extractApiError(err)?.message ??
          "Couldn't remove those right now. Please try again.",
      );
    },
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Add (never remove) — range- and sweep-select are additive, like
  // Google Photos; deselection stays an explicit per-tile toggle.
  const selectAt = (index: number) => {
    const item = list[index];
    if (!item || item.media.display_state !== "processed") return;
    lastIndexRef.current = index;
    setSelected((prev) => new Set(prev).add(item.library_item_id));
  };

  const toggleAt = (index: number, shiftKey: boolean) => {
    if (shiftKey && lastIndexRef.current !== null) {
      const [from, to] = [
        Math.min(lastIndexRef.current, index),
        Math.max(lastIndexRef.current, index),
      ];
      const range = list
        .slice(from, to + 1)
        .filter((i) => i.media.display_state === "processed")
        .map((i) => i.library_item_id);
      setSelected((prev) => new Set([...prev, ...range]));
    } else {
      toggle(list[index]!.library_item_id);
    }
    lastIndexRef.current = index;
  };

  // Sweep-select ends wherever the mouse is released.
  useEffect(() => {
    if (!dragSelecting) return;
    const end = () => setDragSelecting(false);
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, [dragSelecting]);

  // Library search (librarySearchBuild P-LS.6) — while a search is
  // showing, the folders + grid step aside, like the Memilies hub.
  const [searchActive, setSearchActive] = useState(false);

  return (
    <div>
      {!folderId && (
        <LibrarySearch
          onActiveChange={(active) => {
            setSearchActive(active);
            if (active) clearSelection();
          }}
        />
      )}

      {/* Folder strip — root view only */}
      {!folderId && !searchActive && (
        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <SectionLabel>Folders</SectionLabel>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="tap-target font-sans text-[13px] font-medium text-taupe-deep hover:text-ink"
            >
              New folder
            </button>
          </div>
          {folders.length === 0 ? (
            <p className="font-serif text-[13px] italic text-ink-soft">
              Make a folder to keep things organized — drag anything onto
              it.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {folders.map((folder) => (
                <FolderCard
                  key={folder.folder_id}
                  folder={folder}
                  onOpen={() => onOpenFolder(folder.folder_id)}
                  onManage={() => setManagingFolder(folder)}
                  onDropItems={() => {
                    if (dragIds.current.length > 0) {
                      requestMove(dragIds.current, folder);
                      dragIds.current = [];
                    }
                  }}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* In-folder toolbar */}
      {currentFolder && (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <RootDropZone
            onDropItems={() => {
              if (dragIds.current.length > 0) {
                requestMove(dragIds.current, null);
                dragIds.current = [];
              }
            }}
          />
          <QuietButton onClick={() => setManagingFolder(currentFolder)}>
            Folder settings
          </QuietButton>
        </div>
      )}

      {/* Items grid */}
      {!searchActive && (
      <section className="mt-6">
        {!folderId && <SectionLabel>Everything else</SectionLabel>}
        {items.isPending ? (
          <LoadingBlock label="Gathering your library…" />
        ) : items.isError && !items.data ? (
          // Error card ONLY when there's nothing to show — a failed
          // BACKGROUND refetch must not blank a grid the user is
          // looking at (UAT 2026-08-13: 429s made the page flicker
          // between content and this card).
          <Card className="p-6">
            <p className="font-serif text-[14px] italic text-ink-soft">
              We couldn't open your library. Please try again.
            </p>
            <div className="mt-3">
              <QuietButton onClick={() => items.refetch()}>
                Try again
              </QuietButton>
            </div>
          </Card>
        ) : list.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="font-serif text-[15px] italic text-ink-soft">
              {folderId
                ? "Nothing in this folder yet. Drag media here, or import straight into it."
                : "Nothing held here yet. Bring in photos from Google Photos to start."}
            </p>
          </Card>
        ) : (
          <div
            className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 md:grid-cols-6"
            onMouseLeave={() => {
              hoverRef.current = null;
              setHoverIndex(null);
            }}
          >
            {list.map((item, index) => (
              <LibraryTile
                key={item.library_item_id}
                item={item}
                selected={selected.has(item.library_item_id)}
                anySelected={selected.size > 0}
                onToggle={(shiftKey) => {
                  if (suppressClickRef.current) {
                    suppressClickRef.current = false;
                    return;
                  }
                  toggleAt(index, shiftKey);
                }}
                onOpen={() => setOpenIndex(viewable.indexOf(item))}
                onSelectPress={(shiftKey) => {
                  if (shiftKey && lastIndexRef.current !== null) {
                    toggleAt(index, true);
                    suppressClickRef.current = true;
                    return;
                  }
                  // Press begins a sweep; if the press itself selected
                  // the tile, swallow the click that follows on mouseup
                  // so it doesn't immediately toggle back off.
                  if (!selected.has(item.library_item_id)) {
                    selectAt(index);
                    suppressClickRef.current = true;
                  }
                  setDragSelecting(true);
                }}
                previewed={
                  previewRange !== null &&
                  index >= previewRange[0] &&
                  index <= previewRange[1] &&
                  !selected.has(item.library_item_id) &&
                  item.media.display_state === "processed"
                }
                onSweepEnter={() => {
                  hoverRef.current = index;
                  if (dragSelecting) selectAt(index);
                  if (shiftHeld) setHoverIndex(index);
                }}
                onDragStart={() => {
                  dragIds.current = selected.has(item.library_item_id)
                    ? [...selected]
                    : [item.library_item_id];
                }}
              />
            ))}
            {items.hasNextPage && (
              <TailSentinel
                onVisible={() => {
                  if (!items.isFetchingNextPage) void items.fetchNextPage();
                }}
                fetching={items.isFetchingNextPage}
              />
            )}
          </div>
        )}
      </section>
      )}

      <ErrorText>{error}</ErrorText>

      {/* ── Selection action bar (D-LIB.8: Import lives up top; this
             bar manages what's already here). FIXED, not sticky: the
             body scrolls (main's overflow-y-auto never activates), so
             sticky resolved against main's full content-tall box and
             pinned the bar at the very bottom of 1,600 tiles (Dustin,
             UAT 2026-08-14). md offset re-centers over the content
             column, clear of the w-64 nav rail. ── */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 left-1/2 z-30 flex w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 flex-wrap items-center justify-between gap-3 rounded-full bg-ink px-6 py-3 text-white shadow-lg md:bottom-6 md:left-[calc(50%+8rem)]">
          <span className="font-sans text-[14px] font-medium">
            {countLabel(selected.size, "item")} selected
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="tap-target rounded-full bg-white px-5 py-1.5 font-sans text-[13px] font-medium text-ink hover:opacity-90"
            >
              Share as a memory
            </button>
            <MoveMenu
              folders={folders.filter((f) => f.folder_id !== folderId)}
              includeRoot={folderId !== null}
              onPick={(target) => requestMove([...selected], target)}
            />
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="tap-target rounded-full px-4 py-1.5 font-sans text-[13px] text-white/80 hover:text-white"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="tap-target rounded-full px-4 py-1.5 font-sans text-[13px] text-white/80 hover:text-white"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {openIndex !== null && (
        <Lightbox
          items={viewable.map((i) => i.media)}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
        />
      )}

      {creating && (
        <CreateFolderDialog
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}

      {managingFolder && (
        <FolderSettingsDialog
          folder={managingFolder}
          otherFolders={folders.filter(
            (f) => f.folder_id !== managingFolder.folder_id,
          )}
          onClose={() => setManagingFolder(null)}
          onDone={(deleted) => {
            setManagingFolder(null);
            if (deleted && folderId === managingFolder.folder_id) {
              onOpenFolder(null);
            }
            refresh();
          }}
        />
      )}

      {pendingMove && (
        <ConfirmDialog
          title={
            pendingMove.target
              ? `Move to ${pendingMove.target.name}?`
              : "Move out of this folder?"
          }
          body={pendingMove.alert ?? ""}
          confirmLabel="Move"
          busy={moving}
          onConfirm={() => commitMove(pendingMove.ids, pendingMove.target)}
          onCancel={() => setPendingMove(null)}
        />
      )}

      {confirmingDelete && (
        <ConfirmDialog
          title={`Remove ${countLabel(selected.size, "item")} from your library?`}
          body="Anything already shared as a memory stays in your circle's feed — this only removes it from the library."
          confirmLabel="Remove"
          destructive
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}

      {composing && selectedItems.length > 0 && (
        <ComposeMomentOverlay
          items={selectedItems}
          onClose={() => setComposing(false)}
          onPosted={() => {
            setComposing(false);
            clearSelection();
          }}
        />
      )}
    </div>
  );
};

// ── Tiles + folder cards + drop zones ───────────────────────────────

const LibraryTile = ({
  item,
  selected,
  anySelected,
  previewed,
  onToggle,
  onOpen,
  onSelectPress,
  onSweepEnter,
  onDragStart,
}: {
  item: LibraryItem;
  selected: boolean;
  anySelected: boolean;
  previewed: boolean;
  onToggle: (shiftKey: boolean) => void;
  onOpen: () => void;
  onSelectPress: (shiftKey: boolean) => void;
  onSweepEnter: () => void;
  onDragStart: () => void;
}) => {
  const media = item.media;
  const duration = durationLabel(media.duration_seconds);
  const url = tileUrl(media);
  // importPolish P-IP.10: `failed` is TERMINAL — it must not wear the
  // "Still arriving" shimmer forever. It gets its own quiet mark;
  // the import ledger names the file and the remedy.
  const failedState = media.display_state === "failed";
  // Freshly imported items poll until the pipeline finishes; until
  // then the tile is a quiet shimmer and takes no interaction.
  const processing = !failedState && media.display_state !== "processed";
  return (
    <div
      draggable={!processing}
      onDragStart={(e) => {
        if (processing) return;
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onMouseEnter={processing ? undefined : onSweepEnter}
      className={`group relative aspect-square ${selected ? "p-1.5" : ""} transition-[padding]`}
    >
      <button
        type="button"
        disabled={processing || failedState}
        onClick={
          anySelected ? (e) => onToggle(e.shiftKey) : onOpen
        }
        aria-label={
          failedState
            ? "Couldn't be brought over"
            : processing
              ? "Still arriving"
              : anySelected
                ? "Select item"
                : "Open item"
        }
        className="relative block h-full w-full overflow-hidden rounded-lg disabled:cursor-default"
        style={{ backgroundColor: media.dominant_color ?? "#F5F3F0" }}
      >
        {url && !processing && (
          <FocalImage
            src={url}
            focal={focalPoint(media)}
            width={media.width}
            height={media.height}
            className="h-full w-full"
          />
        )}
        {processing && (
          <span className="absolute inset-0 flex animate-pulse items-center justify-center bg-sand/60">
            <span
              aria-hidden
              className="h-5 w-5 animate-spin rounded-full border-2 border-taupe-deep/30 border-t-taupe-deep"
            />
            <span className="sr-only">Still arriving</span>
          </span>
        )}
        {failedState && (
          <span
            title="Couldn't be brought over — see Past imports"
            className="absolute inset-0 flex items-center justify-center bg-sand/50"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              className="text-taupe-deep/70"
              aria-hidden
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M9 9l6 6M15 9l-6 6" />
            </svg>
            <span className="sr-only">Couldn't be brought over</span>
          </span>
        )}
        {!processing &&
          (media.media_kind === "audio" ? (
            <SpeakerGlyph className="absolute bottom-1.5 right-1.5 h-3 w-3 text-white drop-shadow" />
          ) : (
            duration && (
              <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/55 px-1.5 py-0.5 font-mono text-[10px] text-white">
                {duration}
              </span>
            )
          ))}
        {item.scope === "family" && (
          <FamilyMark chip label="Shared with your family" />
        )}
        {/* Shift-hover range preview: a coral wash over the tiles the
            pending shift-click would select. */}
        {previewed && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-lg bg-blush/30 ring-2 ring-inset ring-blush/70"
          />
        )}
      </button>
      {/* Selection checkbox — hover or when a selection is active;
          absent entirely while the item is still processing. Round +
          high-contrast (Dustin 2026-08-14): the white ring separates
          it from any photo, the taupe fill marks selected. Pressing
          it starts a sweep-select; preventDefault on mousedown keeps
          the press from initiating the tile's native drag. */}
      {!processing && !failedState && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelectPress(e.shiftKey);
          }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(e.shiftKey);
          }}
          aria-label={selected ? "Deselect" : "Select"}
          aria-pressed={selected}
          className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full ring-2 transition-opacity ${
            selected
              ? "bg-blush text-white opacity-100 shadow-md ring-white"
              : `bg-ink/20 text-white shadow-sm ring-white/95 focus-visible:opacity-100 group-hover:opacity-100 ${
                  previewed ? "opacity-100" : "opacity-0"
                }`
          }`}
        >
          {selected && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M5 13l5 5L20 7" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};

const FolderCard = ({
  folder,
  onOpen,
  onManage,
  onDropItems,
}: {
  folder: LibraryFolder;
  onOpen: () => void;
  onManage: () => void;
  onDropItems: () => void;
}) => {
  const [dragOver, setDragOver] = useState(false);
  const url = tileUrl(folder.cover);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onDropItems();
      }}
      className={`group relative overflow-hidden rounded-2xl bg-white transition-shadow ${
        dragOver ? "ring-[3px] ring-taupe-deep" : ""
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
      >
        <span
          className="relative block h-24"
          style={{
            backgroundColor:
              folder.cover?.dominant_color ?? "#CDC0B5",
          }}
        >
          {url && (
            <FocalImage
              src={url}
              focal={focalPoint(folder.cover)}
              width={folder.cover?.width}
              height={folder.cover?.height}
              className="h-full w-full"
            />
          )}
          {folder.scope === "family" && (
            <FamilyMark chip label="Shared with your family" />
          )}
        </span>
        <span className="block px-4 py-3">
          <span className="block truncate font-sans text-[14px] font-medium text-ink">
            {folder.name}
          </span>
          <span className="block font-sans text-[12px] text-ink-soft">
            {countLabel(folder.item_count, "item")}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onManage}
        aria-label={`Settings for ${folder.name}`}
        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-ink/35 text-white opacity-0 backdrop-blur-[2px] transition-opacity hover:bg-ink/55 focus-visible:opacity-100 group-hover:opacity-100"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
    </div>
  );
};

const RootDropZone = ({ onDropItems }: { onDropItems: () => void }) => {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onDropItems();
      }}
      className={`rounded-full border border-dashed px-4 py-2 font-sans text-[13px] transition-colors ${
        dragOver
          ? "border-taupe-deep bg-taupe/20 text-ink"
          : "border-sand text-ink-soft"
      }`}
    >
      Drag here to move out of this folder
    </div>
  );
};

const MoveMenu = ({
  folders,
  includeRoot,
  onPick,
}: {
  folders: LibraryFolder[];
  includeRoot: boolean;
  onPick: (target: LibraryFolder | null) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="tap-target rounded-full px-4 py-1.5 font-sans text-[13px] text-white/80 hover:text-white"
      >
        Move to…
      </button>
      {open && (
        <div className="absolute bottom-full right-0 z-20 mb-2 w-56 overflow-hidden rounded-2xl bg-white py-1 shadow-xl">
          {includeRoot && (
            <MoveMenuRow
              label="Library (no folder)"
              onClick={() => {
                setOpen(false);
                onPick(null);
              }}
            />
          )}
          {folders.map((f) => (
            <MoveMenuRow
              key={f.folder_id}
              label={f.name}
              family={f.scope === "family"}
              onClick={() => {
                setOpen(false);
                onPick(f);
              }}
            />
          ))}
          {folders.length === 0 && !includeRoot && (
            <p className="px-4 py-3 font-sans text-[13px] text-ink-soft">
              Make a folder first.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const MoveMenuRow = ({
  label,
  family = false,
  onClick,
}: {
  label: string;
  family?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="tap-target flex w-full items-center gap-2 px-4 py-2.5 text-left font-sans text-[14px] text-ink hover:bg-paper"
  >
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {family && <FamilyMark size={14} label="Family folder" />}
  </button>
);

// ── Folder dialogs ──────────────────────────────────────────────────

const ScopePicker = ({
  scope,
  onChange,
}: {
  scope: LibraryScope;
  onChange: (s: LibraryScope) => void;
}) => (
  <Card className="overflow-hidden border border-sand/40">
    <div role="radiogroup" aria-label="Who can see this folder">
      <RadioRow
        label="Just me"
        sub="Only you can see what's inside."
        selected={scope === "user"}
        onSelect={() => onChange("user")}
      />
      <Hairline />
      <RadioRow
        label="My family"
        sub="Everyone in your circle can see what's inside."
        selected={scope === "family"}
        onSelect={() => onChange("family")}
      />
    </div>
  </Card>
);

const CreateFolderDialog = ({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) => {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<LibraryScope>("user");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createFolder(name.trim(), scope),
    onSuccess: onDone,
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't create the folder. Please try again.",
      ),
  });

  return (
    <Modal title="New folder" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Lake house summers"
          maxLength={80}
          autoFocus
          className="tap-target w-full rounded-2xl border border-sand bg-paper px-4 py-3 font-sans text-[15px] outline-none placeholder:text-ink-soft/60 focus:border-taupe"
        />
        <div className="mt-4">
          <SectionLabel>Who can see it</SectionLabel>
          <ScopePicker scope={scope} onChange={setScope} />
        </div>
        <ErrorText>{error}</ErrorText>
        <div className="mt-6 flex justify-end gap-3">
          <QuietButton onClick={onClose}>Cancel</QuietButton>
          <InkButton type="submit" disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create folder"}
          </InkButton>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Folder settings: rename, scope change (D-LIB.2 folder alert), and
 * delete with the D-LIB.7 disposition prompt.
 */
const FolderSettingsDialog = ({
  folder,
  otherFolders,
  onClose,
  onDone,
}: {
  folder: LibraryFolder;
  otherFolders: LibraryFolder[];
  onClose: () => void;
  onDone: (deleted: boolean) => void;
}) => {
  const [name, setName] = useState(folder.name);
  const [scope, setScope] = useState<LibraryScope>(folder.scope);
  const [confirmingScope, setConfirmingScope] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      updateFolder(folder.folder_id, {
        name: name.trim() !== folder.name ? name.trim() : undefined,
        scope: scope !== folder.scope ? scope : undefined,
      }),
    onSuccess: () => onDone(false),
    onError: (err) => {
      setConfirmingScope(false);
      setError(
        extractApiError(err)?.message ??
          "Couldn't save the folder. Please try again.",
      );
    },
  });

  const commit = () => {
    if (scope !== folder.scope) setConfirmingScope(true);
    else save.mutate();
  };

  if (confirmingScope) {
    return (
      <ConfirmDialog
        title={`Change who can see ${folder.name}?`}
        body={
          scope === "family"
            ? SCOPE_ALERT.folderToFamily
            : SCOPE_ALERT.folderToUser
        }
        confirmLabel="Change"
        busy={save.isPending}
        onConfirm={() => save.mutate()}
        onCancel={() => setConfirmingScope(false)}
      />
    );
  }

  if (deleting) {
    return (
      <DeleteFolderDialog
        folder={folder}
        otherFolders={otherFolders}
        onCancel={() => setDeleting(false)}
        onDone={() => onDone(true)}
      />
    );
  }

  return (
    <Modal title="Folder settings" onClose={onClose}>
      <label className="block">
        <span className="font-sans text-[14px] font-medium text-ink">
          Name
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="tap-target mt-2 w-full rounded-2xl border border-sand bg-paper px-4 py-3 font-sans text-[15px] outline-none focus:border-taupe"
        />
      </label>
      <div className="mt-4">
        <SectionLabel>Who can see it</SectionLabel>
        <ScopePicker scope={scope} onChange={setScope} />
        {scope !== folder.scope && (
          <p className="mt-2 font-sans text-[13px] text-blush">
            {scope === "family"
              ? SCOPE_ALERT.folderToFamily
              : SCOPE_ALERT.folderToUser}
          </p>
        )}
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setDeleting(true)}
          className="tap-target font-sans text-[13px] font-medium text-blush hover:opacity-80"
        >
          Delete folder
        </button>
        <div className="flex gap-3">
          <QuietButton onClick={onClose}>Cancel</QuietButton>
          <InkButton
            onClick={commit}
            disabled={
              save.isPending ||
              !name.trim() ||
              (name.trim() === folder.name && scope === folder.scope)
            }
          >
            {save.isPending ? "Saving…" : "Save"}
          </InkButton>
        </div>
      </div>
    </Modal>
  );
};

/** D-LIB.7 — what happens to the contents. Never silent. */
const DeleteFolderDialog = ({
  folder,
  otherFolders,
  onCancel,
  onDone,
}: {
  folder: LibraryFolder;
  otherFolders: LibraryFolder[];
  onCancel: () => void;
  onDone: () => void;
}) => {
  const [choice, setChoice] = useState<"to_root" | "merge" | "delete_items">(
    "to_root",
  );
  const [mergeTarget, setMergeTarget] = useState<LibraryFolder | null>(
    otherFolders[0] ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => {
      const disposition: FolderDisposition =
        choice === "merge"
          ? { disposition: "merge", target_folder_id: mergeTarget!.folder_id }
          : { disposition: choice };
      return deleteFolder(folder.folder_id, disposition);
    },
    onSuccess: onDone,
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't delete the folder. Please try again.",
      ),
  });

  const empty = folder.item_count === 0;
  const mergeCrossesScope =
    choice === "merge" && mergeTarget && mergeTarget.scope !== folder.scope;

  return (
    <Modal title={`Delete ${folder.name}?`} onClose={onCancel}>
      {empty ? (
        <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
          This folder is empty — it goes away, nothing else changes.
        </p>
      ) : (
        <>
          <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
            What should happen to the{" "}
            {countLabel(folder.item_count, "item")} inside?
          </p>
          <Card className="mt-4 overflow-hidden border border-sand/40">
            <div role="radiogroup" aria-label="What happens to the contents">
              <RadioRow
                label="Keep them in the library"
                sub="They move out of the folder; who can see them doesn't change."
                selected={choice === "to_root"}
                onSelect={() => setChoice("to_root")}
              />
              {otherFolders.length > 0 && (
                <>
                  <Hairline />
                  <RadioRow
                    label="Move them to another folder"
                    sub={
                      mergeTarget
                        ? `They join ${mergeTarget.name}.`
                        : undefined
                    }
                    selected={choice === "merge"}
                    onSelect={() => setChoice("merge")}
                  />
                </>
              )}
              <Hairline />
              <RadioRow
                label="Remove them from the library too"
                sub="Anything already shared as a memory stays in your circle's feed."
                selected={choice === "delete_items"}
                onSelect={() => setChoice("delete_items")}
              />
            </div>
          </Card>

          {choice === "merge" && otherFolders.length > 1 && (
            <div className="mt-3">
              <SectionLabel>Which folder</SectionLabel>
              <Card className="overflow-hidden border border-sand/40">
                <div role="radiogroup" aria-label="Which folder">
                  {otherFolders.map((f, i) => (
                    <div key={f.folder_id}>
                      {i > 0 && <Hairline />}
                      <RadioRow
                        label={f.name}
                        selected={mergeTarget?.folder_id === f.folder_id}
                        onSelect={() => setMergeTarget(f)}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {mergeCrossesScope && (
            <p className="mt-3 font-sans text-[13px] text-blush">
              {mergeTarget!.scope === "family"
                ? SCOPE_ALERT.toFamily
                : SCOPE_ALERT.toUser}
            </p>
          )}
        </>
      )}
      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onCancel}>Cancel</QuietButton>
        <InkButton
          onClick={() => del.mutate()}
          disabled={del.isPending || (choice === "merge" && !mergeTarget)}
        >
          {del.isPending ? "Deleting…" : "Delete folder"}
        </InkButton>
      </div>
    </Modal>
  );
};

// ── Google picker import (D-LIB.8: lands in the Library) ───────────

type ImportStage =
  | { stage: "idle" }
  | { stage: "choose" }
  | { stage: "picking"; sessionId: string; pickerWindow: Window | null }
  | { stage: "confirm"; sessionId: string; count: number }
  | { stage: "archive"; source: ArchiveSource }
  | { stage: "jobs"; plan: ImportPlan };

/**
 * Google's pages refuse framing (frame-ancestors), so a true embedded
 * drawer is off the table — the closest dialog-feel is a sized,
 * centered popup we open and auto-close ourselves the moment the
 * session reports the picks.
 */
const openPickerPopup = (uri: string): Window | null => {
  const width = Math.min(1100, window.screen.availWidth - 80);
  const height = Math.min(780, window.screen.availHeight - 80);
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return window.open(
    uri,
    "memily-google-picker",
    `popup=yes,width=${width},height=${height},left=${Math.max(0, left)},top=${Math.max(0, top)}`,
  );
};

const ImportButton = ({ refresh }: { refresh: () => void }) => {
  const [state, setState] = useState<ImportStage>({ stage: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const queryClient = useQueryClient();
  // Server truth only (GET /oauth/google_photos/status) — connection
  // state must survive refreshes, so it is never held client-side.
  const status = useQuery({
    queryKey: ["google-photos-status"] as const,
    queryFn: googleStatus,
    staleTime: 15_000,
  });
  const connected = status.data?.connected === true;

  // In-flight imports (server truth, same rule as above): the header
  // chip lets the user close the progress modal — or the tab — and
  // find their way back. Polls only while something is running.
  const activeImports = useQuery({
    queryKey: ["library-imports-active"] as const,
    queryFn: listActiveImports,
    refetchInterval: (query) =>
      (query.state.data?.length ?? 0) > 0 ? 5_000 : false,
  });
  const runningJob =
    state.stage === "idle" ? (activeImports.data?.[0] ?? null) : null;

  // When the last running import finishes while only the chip is
  // watching, repaint the grid/folders so the new arrivals show.
  const hadActive = useRef(false);
  useEffect(() => {
    const has = (activeImports.data?.length ?? 0) > 0;
    if (hadActive.current && !has) refresh();
    hadActive.current = has;
  }, [activeImports.data, refresh]);

  const begin = async () => {
    setError(null);
    try {
      const session = await createPickerSession();
      if (!session) {
        // Not connected yet — round-trip through Google consent.
        const { auth_url } = await googleAuthorize();
        window.location.href = auth_url;
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: ["google-photos-status"],
      });
      const pickerWindow = openPickerPopup(session.picker_uri);
      setState({
        stage: "picking",
        sessionId: session.session_id,
        pickerWindow,
      });
    } catch (err) {
      setError(
        httpStatus(err) === 404
          ? "Bringing in photos isn't ready just yet."
          : (extractApiError(err)?.message ??
            "Couldn't reach Google Photos right now. Please try again."),
      );
    }
  };

  const beginPlan = (plan: ImportPlan) => {
    setState({ stage: "jobs", plan });
    // The chip reads server truth — refresh it so closing the modal
    // right away still shows the running import.
    void queryClient.invalidateQueries({
      queryKey: ["library-imports-active"],
    });
  };

  const disconnect = useMutation({
    mutationFn: revokeGoogle,
    onSuccess: () => {
      setDisconnecting(false);
      void queryClient.invalidateQueries({
        queryKey: ["google-photos-status"],
      });
    },
    onError: () => {
      setDisconnecting(false);
      setError("Couldn't disconnect right now. Please try again.");
    },
  });

  return (
    <div className="flex shrink-0 flex-col items-center md:items-end">
      <InkButton onClick={() => setState({ stage: "choose" })}>
        <IconImports className="h-4 w-4" />
        Bring in photos
      </InkButton>
      {/* Disconnect lives inside the source chooser (Dustin
          2026-08-14) — it's a rare governance act, not a header
          affordance. */}
      {runningJob && (
        <button
          type="button"
          onClick={() =>
            beginPlan({
              jobIds: [runningJob.job_id],
              pendingKeys: [],
              scope: "user",
              folderId: null,
            })
          }
          className="tap-target mt-2 flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 shadow-sm transition-shadow hover:shadow"
          aria-label="See how the import is coming along"
        >
          <span
            aria-hidden
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sand border-t-taupe-deep"
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-taupe-deep">
            {runningJob.total > 0
              ? `${runningJob.imported + runningJob.skipped_duplicates} of ${runningJob.total}`
              : "On its way"}
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={() => setLedgerOpen(true)}
        className="tap-target mt-1 font-sans text-[12px] text-ink-soft underline-offset-2 hover:text-ink hover:underline"
      >
        Past imports
      </button>
      <ErrorText>{error}</ErrorText>

      {ledgerOpen && (
        <ImportLedgerDialog onClose={() => setLedgerOpen(false)} />
      )}

      {state.stage === "choose" && (
        <SourceChooserDialog
          connected={connected}
          onClose={() => setState({ stage: "idle" })}
          onGoogle={() => {
            setState({ stage: "idle" });
            void begin();
          }}
          onArchive={(source) => setState({ stage: "archive", source })}
          onDisconnect={() => {
            setState({ stage: "idle" });
            setDisconnecting(true);
          }}
        />
      )}

      {state.stage === "archive" && (
        <ArchiveFlow
          source={state.source}
          onClose={() => setState({ stage: "idle" })}
          onBack={() => setState({ stage: "choose" })}
          onStarted={beginPlan}
        />
      )}

      {disconnecting && (
        <ConfirmDialog
          title="Disconnect Google Photos?"
          body="You can reconnect anytime — and pick a different Google account when you do. Nothing already in your library changes."
          confirmLabel="Disconnect"
          busy={disconnect.isPending}
          onConfirm={() => disconnect.mutate()}
          onCancel={() => setDisconnecting(false)}
        />
      )}

      {state.stage === "picking" && (
        <PickerWait
          sessionId={state.sessionId}
          onCancel={() => {
            state.pickerWindow?.close();
            setState({ stage: "idle" });
          }}
          onPicked={(count) => {
            // Snap back to Memily: close Google's popup ourselves.
            state.pickerWindow?.close();
            setState({ stage: "confirm", sessionId: state.sessionId, count });
          }}
        />
      )}
      {state.stage === "confirm" && (
        <ImportConfirmDialog
          sessionId={state.sessionId}
          count={state.count}
          onClose={() => setState({ stage: "idle" })}
          onStarted={(jobId) =>
            beginPlan({
              jobIds: [jobId],
              pendingKeys: [],
              scope: "user",
              folderId: null,
            })
          }
        />
      )}
      {state.stage === "jobs" && (
        <ImportProgress
          plan={state.plan}
          onDone={() => {
            setState({ stage: "idle" });
            void queryClient.invalidateQueries({
              queryKey: ["library-imports-active"],
            });
            refresh();
          }}
        />
      )}
    </div>
  );
};

/** Waits while the user picks in Google's window (2s poll). */
const PickerWait = ({
  sessionId,
  onCancel,
  onPicked,
}: {
  sessionId: string;
  onCancel: () => void;
  onPicked: (count: number) => void;
}) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const t = setInterval(() => {
      getPickerSession(sessionId)
        .then((s) => {
          if (!alive) return;
          if (s.media_items_set) {
            clearInterval(t);
            onPicked(s.count);
          }
        })
        .catch(() => {
          if (alive)
            setError("We lost track of the Google window. Try again.");
        });
    }, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [sessionId, onPicked]);

  return (
    <Modal title="Choosing in Google Photos" onClose={onCancel}>
      <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
        Pick the photos and videos you'd like to bring home in the Google
        window, then come back here.
      </p>
      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        <div className="mt-5 flex items-center gap-3">
          <span
            aria-hidden
            className="h-5 w-5 animate-spin rounded-full border-2 border-sand border-t-taupe-deep"
          />
          <span className="font-serif text-[14px] italic text-ink-soft">
            Waiting on your picks…
          </span>
        </div>
      )}
      <div className="mt-6 flex justify-end">
        <QuietButton onClick={onCancel}>Cancel</QuietButton>
      </div>
    </Modal>
  );
};

const ImportConfirmDialog = ({
  sessionId,
  count,
  onClose,
  onStarted,
}: {
  sessionId: string;
  count: number;
  onClose: () => void;
  onStarted: (jobId: string) => void;
}) => {
  const folders = useQuery({
    queryKey: ["library-folders"] as const,
    queryFn: listFolders,
    staleTime: 30_000,
  });
  const [scope, setScope] = useState<LibraryScope>("user");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetFolder =
    (folderId &&
      (folders.data ?? []).find((f) => f.folder_id === folderId)) ||
    null;
  // Importing into a folder inherits the folder's scope (D-LIB.1).
  const effectiveScope: LibraryScope = targetFolder
    ? targetFolder.scope
    : scope;

  const start = useMutation({
    mutationFn: () =>
      startLibraryImport({
        provider: "google_photos",
        session_id: sessionId,
        scope: effectiveScope,
        folder_id: folderId ?? undefined,
      }),
    onSuccess: ({ job_id }) => onStarted(job_id),
    onError: (err) => {
      const apiErr = extractApiError(err);
      setError(
        httpStatus(err) === 402
          ? (apiErr?.message ??
            "Your keeper is full on your current plan. Upgrade to keep more photos.")
          : (apiErr?.message ??
            "Couldn't start bringing those in. Please try again."),
      );
    },
  });

  return (
    <Modal
      title={`Bring in ${countLabel(count, "item")}`}
      onClose={onClose}
    >
      <div>
        <SectionLabel>Where they go</SectionLabel>
        <Card className="overflow-hidden border border-sand/40">
          <div role="radiogroup" aria-label="Where they go">
            <RadioRow
              label="Library"
              sub="No folder — organize them later."
              selected={folderId === null}
              onSelect={() => setFolderId(null)}
            />
            {(folders.data ?? []).map((f) => (
              <div key={f.folder_id}>
                <Hairline />
                <RadioRow
                  label={f.name}
                  sub={
                    f.scope === "family"
                      ? "A family folder — everyone in your circle sees these."
                      : "A private folder — just you."
                  }
                  selected={folderId === f.folder_id}
                  onSelect={() => setFolderId(f.folder_id)}
                />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {folderId === null && (
        <div className="mt-4">
          <SectionLabel>Who can see them</SectionLabel>
          <ScopePicker scope={scope} onChange={setScope} />
        </div>
      )}

      {targetFolder && targetFolder.scope === "family" && (
        <p className="mt-3 font-sans text-[13px] text-blush">
          {SCOPE_ALERT.toFamily}
        </p>
      )}

      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onClose}>Cancel</QuietButton>
        <InkButton onClick={() => start.mutate()} disabled={start.isPending}>
          {start.isPending
            ? "Starting…"
            : `Bring in ${countLabel(count, "item")}`}
        </InkButton>
      </div>
    </Modal>
  );
};

/** Poll the durable import job (2s, the data-export pattern). */
// importPolish P-IP.9 — say WHY things skipped; bare "43 skipped"
// read as loss to a user protecting their memories (live UAT).
const skipSentence = (job: LibraryImportJob): string => {
  const r = job.skip_reasons;
  const parts: string[] = [];
  if (r.duplicate_in_file > 0)
    parts.push(`${r.duplicate_in_file} appeared in the file more than once`);
  if (r.already_imported > 0)
    parts.push(`${r.already_imported} brought in before`);
  if (r.same_photo_elsewhere > 0)
    parts.push(`${r.same_photo_elsewhere} already here from another source`);
  if (parts.length === 0) {
    return `${countLabel(job.skipped_duplicates, "item")} skipped — copies of photos already safely here. Nothing is lost.`;
  }
  return `${countLabel(job.skipped_duplicates, "item")} skipped — ${parts.join(
    ", ",
  )}. We keep one of each; nothing is lost.`;
};

// importPolish P-IP.8 — the ledger names sources in customer words.
const importSourceLabel = (job: LibraryImportJob): string =>
  job.provider === "google_photos"
    ? "Google Photos"
    : job.detected_format === "meta_dyi"
      ? "Facebook or Instagram"
      : job.detected_format === "takeout"
        ? "Google Takeout"
        : job.detected_format === "icloud"
          ? "Apple iCloud"
          : "Your computer";

/** importPolish P-IP.3 — one import can be MANY files. Already-started
 *  jobs (google picker) ride `jobIds`; staged archives wait in
 *  `pendingKeys` and start SEQUENTIALLY — one job at a time keeps the
 *  worker's disk honest (two 10 GB zips would not co-exist on
 *  Fargate's ephemeral storage). */
export interface ImportPlan {
  jobIds: string[];
  pendingKeys: string[];
  scope: LibraryScope;
  folderId: string | null;
}

const TERMINAL = new Set(["done", "failed", "partial"]);

const ImportProgress = ({
  plan,
  onDone,
}: {
  plan: ImportPlan;
  onDone: () => void;
}) => {
  const [jobIds, setJobIds] = useState(plan.jobIds);
  const [pendingKeys, setPendingKeys] = useState(plan.pendingKeys);
  const [job, setJob] = useState<LibraryImportJob | null>(null);
  const [settled, setSettled] = useState<LibraryImportJob[]>([]);
  const [startError, setStartError] = useState<string | null>(null);
  const startingRef = useRef(false);
  const jobId = jobIds[jobIds.length - 1] ?? null;
  const totalFiles = jobIds.length + pendingKeys.length;

  // Sequential starter: no live job (or the live one settled) and
  // keys remain → start the next archive's job.
  const jobTerminal = job !== null && TERMINAL.has(job.state);
  useEffect(() => {
    if (startingRef.current || pendingKeys.length === 0) return;
    if (jobId !== null && !jobTerminal) return;
    startingRef.current = true;
    if (job && jobTerminal) setSettled((prev) => [...prev, job]);
    startLibraryImport({
      provider: "archive",
      archive_key: pendingKeys[0]!,
      scope: plan.scope,
      folder_id: plan.folderId ?? undefined,
    })
      .then(({ job_id }) => {
        setJobIds((prev) => [...prev, job_id]);
        setPendingKeys((prev) => prev.slice(1));
        setJob(null);
      })
      .catch((err: unknown) => {
        setStartError(
          extractApiError(err)?.message ??
            "The next file couldn't be started. Everything already brought in is safe.",
        );
        setPendingKeys([]);
      })
      .finally(() => {
        startingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, jobTerminal, pendingKeys.length]);

  useEffect(() => {
    if (jobId === null) return;
    let alive = true;
    const t = setInterval(() => {
      getLibraryImportJob(jobId)
        .then((j) => {
          if (!alive) return;
          setJob(j);
          if (TERMINAL.has(j.state)) clearInterval(t);
        })
        .catch(() => {});
    }, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [jobId]);

  const finished =
    pendingKeys.length === 0 &&
    startError === null &&
    job !== null &&
    TERMINAL.has(job.state);

  // Aggregate across every settled file plus the live one — the
  // All-set numbers speak for the whole batch.
  const allJobs = [...settled, ...(job ? [job] : [])];
  const agg = allJobs.reduce(
    (a, j) => ({
      total: a.total + j.total,
      imported: a.imported + j.imported,
      failed: a.failed + j.failed,
      skipped_duplicates: a.skipped_duplicates + j.skipped_duplicates,
      skip_reasons: {
        duplicate_in_file:
          a.skip_reasons.duplicate_in_file +
          j.skip_reasons.duplicate_in_file,
        already_imported:
          a.skip_reasons.already_imported + j.skip_reasons.already_imported,
        same_photo_elsewhere:
          a.skip_reasons.same_photo_elsewhere +
          j.skip_reasons.same_photo_elsewhere,
      },
    }),
    {
      total: 0,
      imported: 0,
      failed: 0,
      skipped_duplicates: 0,
      skip_reasons: {
        duplicate_in_file: 0,
        already_imported: 0,
        same_photo_elsewhere: 0,
      },
    },
  );
  const pct =
    job && job.total > 0
      ? Math.min(100, Math.round((job.imported / job.total) * 100))
      : 0;
  const summary = finished || startError ? agg : null;

  return (
    <Modal
      title={finished ? "All set" : "Bringing them home"}
      onClose={onDone}
    >
      {!job && !startError ? (
        <LoadingBlock label="Starting up…" />
      ) : summary ? (
        <div>
          <p className="font-sans text-[14px] font-medium text-ink">
            {countLabel(summary.imported, "item")} brought in
            {totalFiles > 1 &&
              ` across ${countLabel(jobIds.length, "file")}`}
            .
          </p>
          {summary.skipped_duplicates > 0 && (
            <p className="mt-2 font-sans text-[13px] text-ink-soft">
              {skipSentence({
                skipped_duplicates: summary.skipped_duplicates,
                skip_reasons: summary.skip_reasons,
              } as LibraryImportJob)}
            </p>
          )}
          {summary.failed > 0 && (
            <p className="mt-1 font-sans text-[13px] text-blush">
              {countLabel(summary.failed, "item")} couldn't come over —
              Past imports has their names.
            </p>
          )}
          {startError && <ErrorText>{startError}</ErrorText>}
          {!startError &&
            job?.state === "failed" &&
            job.error_reason && <ErrorText>{job.error_reason}</ErrorText>}
        </div>
      ) : (
        <div>
          {totalFiles > 1 && (
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-taupe-deep">
              File {Math.min(jobIds.length, totalFiles)} of {totalFiles}
            </p>
          )}
          <div className="flex items-baseline justify-between">
            <p className="font-sans text-[14px] font-medium text-ink">
              {job!.state === "queued"
                ? "Waiting to start…"
                : job!.state === "downloading"
                  ? "Bringing in your originals, full quality."
                  : job!.state === "failed"
                    ? "Something went wrong."
                    : "Done."}
            </p>
            <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-taupe-deep">
              {job!.imported} of {job!.total}
            </p>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-sand/60">
            <div
              className="h-1.5 rounded-full bg-taupe transition-all"
              style={{ width: `${Math.max(4, pct)}%` }}
            />
          </div>
          {job!.skipped_duplicates > 0 && (
            <p className="mt-2 font-sans text-[13px] text-ink-soft">
              {skipSentence(job!)}
            </p>
          )}
        </div>
      )}
      <div className="mt-6 flex justify-end">
        {finished || startError ? (
          <InkButton onClick={onDone}>Okay</InkButton>
        ) : (
          <QuietButton onClick={onDone}>Keep working</QuietButton>
        )}
      </div>
    </Modal>
  );
};

/** importPolish P-IP.8 — the import ledger: every import, newest
 *  first. Answers "did I already bring in 2019?" and names anything
 *  that couldn't come over (P-IP.10), with the remedy. */
const ImportLedgerDialog = ({ onClose }: { onClose: () => void }) => {
  const history = useInfiniteQuery({
    queryKey: ["library-import-history"] as const,
    queryFn: ({ pageParam }) => listImportHistory(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const jobs = history.data?.pages.flatMap((p) => p.jobs) ?? [];

  return (
    <Modal title="What you've brought in" onClose={onClose} wide>
      {history.isPending ? (
        <LoadingBlock label="Opening the record…" />
      ) : history.isError ? (
        <p className="font-serif text-[14px] italic text-ink-soft">
          The record isn't available just now. Please try again.
        </p>
      ) : jobs.length === 0 ? (
        <p className="font-serif text-[14px] italic text-ink-soft">
          Nothing brought in yet — everything will be listed here.
        </p>
      ) : (
        <div>
          {jobs.map((job, i) => (
            <div key={job.job_id}>
              {i > 0 && <Hairline />}
              <div className="py-4">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-sans text-[15px] font-medium text-ink">
                    {importSourceLabel(job)}
                  </p>
                  <p className="shrink-0 font-mono text-[11px] uppercase tracking-[0.14em] text-taupe-deep">
                    {job.created_at
                      ? new Date(job.created_at).toLocaleDateString(
                          undefined,
                          {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          },
                        )
                      : ""}
                  </p>
                </div>
                {job.state === "failed" ? (
                  <p className="mt-1 font-sans text-[13px] text-blush">
                    Didn't finish —{" "}
                    {job.error_reason ?? "something went wrong."}
                  </p>
                ) : (
                  <p className="mt-1 font-sans text-[13px] text-ink-soft">
                    {countLabel(job.imported, "item")} brought in
                    {job.skipped_duplicates > 0 &&
                      ` · ${job.skipped_duplicates} skipped`}
                    {job.failed > 0 && ` · ${job.failed} couldn't come over`}
                  </p>
                )}
                {job.skipped_duplicates > 0 && (
                  <p className="mt-1 font-sans text-[12px] text-ink-soft/80">
                    {skipSentence(job)}
                  </p>
                )}
                {job.failed_filenames.length > 0 && (
                  <div className="mt-2 rounded-xl bg-paper px-4 py-3">
                    <p className="font-sans text-[12px] font-medium text-ink">
                      Couldn't come over:
                    </p>
                    <p className="mt-1 break-words font-mono text-[11px] leading-[18px] text-taupe-deep">
                      {job.failed_filenames.join(" · ")}
                    </p>
                    <p className="mt-2 font-sans text-[12px] text-ink-soft">
                      Bring the file in again — everything already here
                      is skipped, so only these will be tried.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
          {history.hasNextPage && (
            <div className="flex justify-center pb-2">
              <QuietButton
                onClick={() => void history.fetchNextPage()}
                disabled={history.isFetchingNextPage}
              >
                {history.isFetchingNextPage ? "Opening…" : "Show more"}
              </QuietButton>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// ── Archive import (archiveImportBuild.md — D-AR.1–8) ──────────────
//
// Meta OAuth is skipped permanently (D-AR.1): the Facebook/Instagram
// story is the "Download Your Information" archive, Google's is
// Takeout (which keeps GPS the live picker strips), and a plain
// zipped folder covers everything else. One pipeline; the worker
// sniffs the format (D-AR.2/3). The web surface leads with a guided
// walkthrough because *requesting the export* is the hard part for
// this audience (D-AR.4).

type ArchiveSource = "meta" | "takeout" | "icloud" | "folder";

// The folder source's iOS face (one row, OS-aware — Dustin
// 2026-08-16: "OS aware instructions… NOT needing its own row").
// The native picker sheet behind the file input carries Photos
// search, People, and albums.
const IPHONE_GUIDE = {
  title: "Bring in photos from this iPhone",
  intro:
    "Pick straight from your photo library — no zipping, no computer.",
  steps: [
    "Tap Choose from your photos below — your photo library opens right here, search and albums included.",
    "Pick as many as you like. We'll gather them up and bring them in.",
  ],
};

const ARCHIVE_GUIDES: Record<
  ArchiveSource,
  { title: string; intro: string; steps: string[] }
> = {
  meta: {
    title: "Bring in from Facebook or Instagram",
    intro:
      "Facebook keeps a copy of everything you've shared. Here's how to ask for it — it's your history, and they have to hand it over.",
    steps: [
      "Visit facebook.com/dyi — or, for Instagram, open Accounts Center and choose Download your information.",
      "Choose Download or transfer information, then Some of your information, and tick Posts and Photos and videos.",
      "When asked how, pick Download to device, JSON format, and High media quality.",
      "Facebook takes a while to gather everything — often a few hours, sometimes a day. They'll email you when your file is ready.",
      "Download the file, then come back here and hand it to us below.",
    ],
  },
  takeout: {
    title: "Bring in from Google Takeout",
    intro:
      "Takeout is Google's way of giving you everything at once — full quality, with dates and places kept.",
    steps: [
      "Visit takeout.google.com and sign in.",
      "Choose Deselect all, then tick only Google Photos.",
      "Choose Export once, and pick 10 GB as the file size — Google will split your photos into files we can take one at a time.",
      "Google will email you when it's ready — usually within a day.",
      "Download it, then come back and drop the files below — all of them at once is fine.",
    ],
  },
  icloud: {
    title: "Bring in from Apple iCloud",
    intro:
      "Apple keeps a full copy of your iCloud photos — here's how to ask for it. Live Photos arrive whole, motion and all.",
    steps: [
      "Visit privacy.apple.com and sign in with your Apple ID.",
      "Choose Request a copy of your data, then tick iCloud Photos.",
      "Pick a file size like 5 GB — Apple will split your photos into files we can take one at a time.",
      "Apple takes a few days — sometimes up to a week — and emails you when it's ready.",
      "Download the files, then come back and drop them all below — we'll take them one after another.",
    ],
  },
  folder: {
    title: "Bring in photos from your computer",
    intro:
      "One photo or a whole drive's worth — from a camera card, an old backup, anywhere.",
    steps: [
      "A few photos or videos? Just drop them below — no zipping needed.",
      "A whole folder? Compress it first: right-click the folder and choose Compress on a Mac, or Send to → Compressed folder on Windows.",
      "Hand us your photos — or the compressed file — below.",
    ],
  },
};

const SourceChooserDialog = ({
  connected,
  onClose,
  onGoogle,
  onArchive,
  onDisconnect,
}: {
  connected: boolean;
  onClose: () => void;
  onGoogle: () => void;
  onArchive: (source: ArchiveSource) => void;
  onDisconnect: () => void;
}) => (
  <Modal title="Bring in photos" onClose={onClose}>
    <p className="font-serif text-[14px] italic text-ink-soft">
      Wherever your history lives, it can come home.
    </p>
    <div className="mt-4 overflow-hidden rounded-2xl border border-sand/40">
      <SourceRow
        icon={<IconGooglePhotos className="h-5 w-5" />}
        label="Google Photos"
        sub="Connect and pick — the quickest way."
        onClick={onGoogle}
      />
      <Hairline />
      <SourceRow
        icon={<IconFacebook className="h-5 w-5" />}
        label="Facebook or Instagram"
        sub="From a saved copy of your information."
        onClick={() => onArchive("meta")}
      />
      <Hairline />
      <SourceRow
        icon={<IconGooglePhotos className="h-5 w-5" />}
        label="Google Takeout"
        sub="Everything at once, at full quality."
        onClick={() => onArchive("takeout")}
      />
      <Hairline />
      <SourceRow
        icon={<IconApple className="h-5 w-5" />}
        label="Apple iCloud"
        sub="From Apple's copy of your photos."
        onClick={() => onArchive("icloud")}
      />
      <Hairline />
      <SourceRow
        icon={<IconImports className="h-5 w-5" />}
        label={
          IS_IOS ? "Photos on this iPhone or iPad" : "Photos from your computer"
        }
        sub={
          IS_IOS
            ? "Pick straight from your photos."
            : "One photo, a few, or a whole zipped folder."
        }
        onClick={() => onArchive("folder")}
      />
    </div>
    {connected && (
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onDisconnect}
          className="tap-target font-sans text-[12px] text-ink-soft underline-offset-2 hover:text-ink hover:underline"
        >
          Disconnect Google Photos
        </button>
      </div>
    )}
  </Modal>
);

const SourceRow = ({
  icon,
  label,
  sub,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="tap-target flex w-full items-center gap-4 bg-white px-5 py-4 text-left transition-colors hover:bg-paper/60"
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-paper text-ink">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-sans text-[15px] font-medium text-ink">
        {label}
      </span>
      <span className="block font-sans text-[13px] text-ink-soft">{sub}</span>
    </span>
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-taupe-deep"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  </button>
);

// 16 GiB hard ceiling (archiveImportBuild D-AR.10 — the worker
// unpacks on bounded local disk). The server enforces its own; this
// only spares a doomed init call.
const ARCHIVE_MAX_BYTES = 16 * 1024 ** 3;

const gbLabel = (bytes: number): string =>
  bytes >= 1024 ** 3
    ? `${(bytes / 1024 ** 3).toFixed(1)} GB`
    : bytes >= 1024 ** 2
      ? `${Math.round(bytes / 1024 ** 2)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

type ArchiveStage = "guide" | "destination" | "uploading";

/**
 * Walkthrough → file → destination → upload (auto-starts the job on
 * completion). Destination is asked BEFORE the upload so a 50 GB
 * file can run unattended straight into the job-progress modal.
 */
const ArchiveFlow = ({
  source,
  onClose,
  onBack,
  onStarted,
}: {
  source: ArchiveSource;
  onClose: () => void;
  onBack: () => void;
  onStarted: (plan: ImportPlan) => void;
}) => {
  const guide =
    source === "folder" && IS_IOS ? IPHONE_GUIDE : ARCHIVE_GUIDES[source];
  // On a Mac, the Photos app hands photos to any browser by drag —
  // so its search (people, places, dates) becomes the picker. A tip,
  // not a source: the pipeline is identical (Dustin 2026-08-16).
  const guideSteps =
    source === "folder" && IS_MAC
      ? [
          ...guide.steps,
          "Keep your pictures in the Photos app? Search there — a person, a place, a year — select what you find, and drag it straight onto the box below.",
        ]
      : guide.steps;
  const [stage, setStage] = useState<ArchiveStage>("guide");
  // P-IP.3: a pick is a BATCH — zips as-is, loose media bundled into
  // one more archive client-side at upload time.
  const [archives, setArchives] = useState<File[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [ignored, setIgnored] = useState(0);
  const [fileIndex, setFileIndex] = useState(0);
  const uploadListRef = useRef<File[] | null>(null);
  const uploadedKeysRef = useRef<string[]>([]);
  const [scope, setScope] = useState<LibraryScope>("user");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const folders = useQuery({
    queryKey: ["library-folders"] as const,
    queryFn: listFolders,
    staleTime: 30_000,
  });
  // T-5: warn BEFORE a doomed upload when the pick visibly exceeds
  // the plan's room. Loose photos have knowable counts; zips don't
  // (the worker's gate stays the truth either way).
  const subscription = useQuery(subscriptionQuery());
  const pictures = subscription.data?.entitlements.pictures;
  const capShort =
    mediaFiles.length > 0 &&
    pictures?.cap != null &&
    pictures.used != null &&
    mediaFiles.length > pictures.cap - pictures.used;
  const targetFolder =
    (folderId &&
      (folders.data ?? []).find((f) => f.folder_id === folderId)) ||
    null;
  // Importing into a folder inherits the folder's scope (D-LIB.1).
  const effectiveScope: LibraryScope = targetFolder
    ? targetFolder.scope
    : scope;

  const pick = (files: File[]) => {
    setError(null);
    const picked = classifyArchivePick(files);
    if (picked.kind === "invalid") {
      setError(
        "We can take photos, videos, audio, or compressed (.zip) files.",
      );
      return;
    }
    // The ceiling is per ARCHIVE (each becomes its own upload + job);
    // loose media bundles into one archive, so its sum counts.
    const oversized = picked.archives.find(
      (f) => f.size > ARCHIVE_MAX_BYTES,
    );
    const mediaSize = picked.media.reduce((sum, f) => sum + f.size, 0);
    if (oversized || mediaSize > ARCHIVE_MAX_BYTES) {
      setError(
        oversized
          ? `${oversized.name} is bigger than we can take in one piece — export it in smaller parts and drop them all here together.`
          : "That's more photos than we can take in one batch — bring them in a couple of batches.",
      );
      return;
    }
    uploadListRef.current = null;
    uploadedKeysRef.current = [];
    setArchives(picked.archives);
    setMediaFiles(picked.media);
    setIgnored(picked.ignored);
    setStage("destination");
  };

  const begin = async () => {
    setStage("uploading");
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Build the upload list once; a retry resumes where it stopped
      // (already-uploaded archives keep their staged keys).
      if (uploadListRef.current === null) {
        const bundle =
          mediaFiles.length > 0 ? [await zipMediaFiles(mediaFiles)] : [];
        uploadListRef.current = [...archives, ...bundle];
      }
      const list = uploadListRef.current;
      for (let i = uploadedKeysRef.current.length; i < list.length; i++) {
        setFileIndex(i);
        setProgress(0);
        const key = await uploadArchive(
          list[i]!,
          setProgress,
          controller.signal,
        );
        uploadedKeysRef.current.push(key);
      }
      // Jobs start SEQUENTIALLY in the progress surface — one at a
      // time keeps the worker's ephemeral disk honest (P-IP.3).
      onStarted({
        jobIds: [],
        pendingKeys: uploadedKeysRef.current,
        scope: effectiveScope,
        folderId: folderId ?? null,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const apiErr = extractApiError(err);
      setError(
        httpStatus(err) === 404
          ? "Bringing in a saved file isn't ready just yet."
          : (apiErr?.message ??
            "The file didn't make it all the way. Check your connection and try again."),
      );
    }
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
    onClose();
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  if (stage === "guide") {
    return (
      <Modal title={guide.title} onClose={onClose} onBack={onBack} wide>
        <p className="font-serif text-[14px] italic leading-[22px] text-ink-soft">
          {guide.intro}
        </p>
        <ol className="mt-4 space-y-3">
          {guideSteps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-paper font-mono text-[11px] text-taupe-deep">
                {i + 1}
              </span>
              <span className="font-sans text-[14px] leading-[22px] text-ink">
                {step}
              </span>
            </li>
          ))}
        </ol>
        <FileDropZone
          onPick={pick}
          pickLabel={
            IS_IOS ? "Choose from your photos" : "Choose from your computer"
          }
        />
        <ErrorText>{error}</ErrorText>
        <div className="mt-6 flex justify-end">
          <QuietButton onClick={onClose}>Not yet</QuietButton>
        </div>
      </Modal>
    );
  }

  const pickedCount = archives.length + (mediaFiles.length > 0 ? 1 : 0);
  const pickedSize = [...archives, ...mediaFiles].reduce(
    (sum, f) => sum + f.size,
    0,
  );
  if (stage === "destination" && pickedCount > 0) {
    return (
      <Modal
        title="Where they go"
        onClose={onClose}
        onBack={() => setStage("guide")}
      >
        <p className="flex items-center gap-2 rounded-2xl bg-paper px-4 py-3 font-sans text-[14px] text-ink">
          <span className="min-w-0 flex-1 truncate font-medium">
            {pickedCount === 1 && archives.length === 1
              ? archives[0]!.name
              : archives.length === 0
                ? countLabel(mediaFiles.length, "photo")
                : `${countLabel(archives.length, "file")}${
                    mediaFiles.length > 0
                      ? ` + ${countLabel(mediaFiles.length, "photo")}`
                      : ""
                  }`}
          </span>
          <span className="shrink-0 font-mono text-[12px] text-taupe-deep">
            {gbLabel(pickedSize)}
          </span>
        </p>
        {ignored > 0 && (
          <p className="mt-2 font-sans text-[12px] text-ink-soft">
            We left out {countLabel(ignored, "file")} that aren't photos,
            videos, or audio.
          </p>
        )}
        {capShort && (
          <p className="mt-2 font-sans text-[12px] text-blush">
            This looks like more than your plan has room for — we'll
            bring in what fits, and the rest will wait for an upgrade.
          </p>
        )}
        <div className="mt-4">
          <SectionLabel>Which folder</SectionLabel>
          <Card className="overflow-hidden border border-sand/40">
            <div role="radiogroup" aria-label="Which folder">
              <RadioRow
                label="Library"
                sub="No folder — organize them later."
                selected={folderId === null}
                onSelect={() => setFolderId(null)}
              />
              {(folders.data ?? []).map((f) => (
                <div key={f.folder_id}>
                  <Hairline />
                  <RadioRow
                    label={f.name}
                    sub={
                      f.scope === "family"
                        ? "A family folder — everyone in your circle sees these."
                        : "A private folder — just you."
                    }
                    selected={folderId === f.folder_id}
                    onSelect={() => setFolderId(f.folder_id)}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {folderId === null && (
          <div className="mt-4">
            <SectionLabel>Who can see them</SectionLabel>
            <ScopePicker scope={scope} onChange={setScope} />
          </div>
        )}

        {targetFolder && targetFolder.scope === "family" && (
          <p className="mt-3 font-sans text-[13px] text-blush">
            {SCOPE_ALERT.toFamily}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <InkButton onClick={() => void begin()}>
            Start bringing them in
          </InkButton>
        </div>
      </Modal>
    );
  }

  // Uploading — the file is on its way to the shelf; the import job
  // takes over the moment it lands (onStarted → ImportProgress).
  return (
    <Modal title="On its way" onClose={cancelUpload}>
      {uploadListRef.current !== null &&
        uploadListRef.current.length > 1 && (
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-taupe-deep">
            File {Math.min(fileIndex + 1, uploadListRef.current.length)} of{" "}
            {uploadListRef.current.length}
          </p>
        )}
      <div className="flex items-baseline justify-between">
        <p className="font-sans text-[14px] font-medium text-ink">
          {error
            ? "Something went wrong."
            : pickedCount > 1
              ? "Sending your files, safely."
              : "Sending your file, safely."}
        </p>
        <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-taupe-deep">
          {Math.round(progress * 100)}%
        </p>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-sand/60">
        <div
          className="h-1.5 rounded-full bg-taupe transition-all"
          style={{ width: `${Math.max(4, Math.round(progress * 100))}%` }}
        />
      </div>
      <p className="mt-3 font-serif text-[13px] italic text-ink-soft">
        Big files take a while — you can keep this window open and step
        away.
      </p>
      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        {error ? (
          <>
            <QuietButton onClick={onClose}>Not now</QuietButton>
            <InkButton onClick={() => void begin()}>Try again</InkButton>
          </>
        ) : (
          <QuietButton onClick={cancelUpload}>Cancel</QuietButton>
        )}
      </div>
    </Modal>
  );
};

/** Dashed drop target + browse button. Takes one zip, or loose
 *  photos/videos/audio (zipped client-side before upload). */
const FileDropZone = ({
  onPick,
  pickLabel = "Choose from your computer",
}: {
  onPick: (files: File[]) => void;
  pickLabel?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        // P-IP.7: folders traverse (webkitGetAsEntry); the handles
        // must be taken before the event dies, hence the helper owns
        // the whole DataTransfer.
        void collectDroppedFiles(e.dataTransfer).then((files) => {
          if (files.length > 0) onPick(files);
        });
      }}
      className={`mt-5 flex flex-col items-center gap-2 rounded-2xl border border-dashed px-6 py-8 text-center transition-colors ${
        dragOver ? "border-taupe-deep bg-taupe/10" : "border-sand"
      }`}
    >
      <p className="font-sans text-[14px] text-ink">
        Drop your photos or file here, or
      </p>
      <QuietButton onClick={() => inputRef.current?.click()}>
        {pickLabel}
      </QuietButton>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".zip,application/zip,image/*,video/*,audio/*"
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          if (files.length > 0) onPick(files);
          e.target.value = "";
        }}
      />
    </div>
  );
};

// ── Library search (librarySearchBuild P-LS.6) ─────────────────────
//
// POST /media/search with scope pinned "library" — the caller's own
// Library slice of the media index. One `q` reaches captions, album
// names, labels, OCR, transcripts, people, and places (D-LS.2 parity);
// kind pills refine. Results are view-only v1: tap opens the
// lightbox; organizing stays with the grid.

const KIND_PILLS = [
  [null, "Everything"],
  ["image", "Photos"],
  ["video", "Videos"],
  ["audio", "Audio"],
] as const;

const LibrarySearch = ({
  onActiveChange,
}: {
  onActiveChange: (active: boolean) => void;
}) => {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [kind, setKind] = useState<"image" | "video" | "audio" | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const active = submitted !== null;
  const results = useInfiniteQuery({
    queryKey: ["library-search", submitted, kind] as const,
    queryFn: ({ pageParam }) =>
      searchLibrary({
        q: submitted ?? undefined,
        media_kind: kind ?? undefined,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: active,
  });
  const found = results.data?.pages.flatMap((p) => p.items) ?? [];

  const run = () => {
    const q = query.trim();
    if (!q) return;
    setSubmitted(q);
    onActiveChange(true);
  };
  const clear = () => {
    setQuery("");
    setSubmitted(null);
    setKind(null);
    setOpenIndex(null);
    onActiveChange(false);
  };

  return (
    <div className="mt-8">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
        className="flex items-center gap-3 rounded-full bg-white px-5 py-2.5 shadow-sm"
      >
        <IconSearch className="h-5 w-5 shrink-0 text-taupe-deep" />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!e.target.value) clear();
          }}
          placeholder="Search your library"
          aria-label="Search your library"
          enterKeyHint="search"
          className="w-full bg-transparent font-sans text-[15px] outline-none placeholder:text-ink-soft"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="tap-target -mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-taupe-deep hover:bg-paper"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </form>

      {query && !active && (
        <p className="mt-3 font-serif text-[13px] italic text-ink-soft">
          Try what they wrote back then — a caption, an album, a place,
          or who's in it.
        </p>
      )}

      {active && (
        <div className="mt-4">
          <div role="tablist" aria-label="What kind" className="flex gap-2">
            {KIND_PILLS.map(([value, label]) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={kind === value}
                onClick={() => setKind(value)}
                className={`tap-target rounded-full px-4 py-1.5 font-sans text-[13px] font-medium transition-colors ${
                  kind === value
                    ? "bg-ink text-white"
                    : "bg-white text-ink-soft hover:bg-sand/30"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {results.isPending ? (
              <LoadingBlock label="Looking through your library…" />
            ) : results.isError ? (
              <div className="py-8 text-center">
                <p className="mb-3 font-serif text-[15px] italic text-ink-soft">
                  We couldn't search just now. Please try again.
                </p>
                <QuietButton onClick={() => void results.refetch()}>
                  Try again
                </QuietButton>
              </div>
            ) : found.length === 0 ? (
              <p className="py-8 text-center font-serif text-[15px] italic text-ink-soft">
                Nothing here matches that — try a different word, or
                fewer of them.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 md:grid-cols-6">
                {found.map((item, index) => (
                  <SearchResultTile
                    key={item.item_id}
                    item={item}
                    onOpen={() => setOpenIndex(index)}
                  />
                ))}
                {results.hasNextPage && (
                  <TailSentinel
                    onVisible={() => {
                      if (!results.isFetchingNextPage) {
                        void results.fetchNextPage();
                      }
                    }}
                    fetching={results.isFetchingNextPage}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {openIndex !== null && (
        <Lightbox
          items={found}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
        />
      )}
    </div>
  );
};

/** The grid tile's visual language, minus selection/drag — search
 *  results are view-only v1. */
const SearchResultTile = ({
  item,
  onOpen,
}: {
  item: MediaSearchItem;
  onOpen: () => void;
}) => {
  const duration = durationLabel(item.duration_seconds);
  const url = tileUrl(item);
  return (
    <div className="relative aspect-square">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open item"
        className="relative block h-full w-full overflow-hidden rounded-lg"
        style={{ backgroundColor: item.dominant_color ?? "#F5F3F0" }}
      >
        {url && (
          <FocalImage
            src={url}
            focal={focalPoint(item)}
            width={item.width}
            height={item.height}
            className="h-full w-full"
          />
        )}
        {item.media_kind === "audio" ? (
          <SpeakerGlyph className="absolute bottom-1.5 right-1.5 h-3 w-3 text-white drop-shadow" />
        ) : (
          duration && (
            <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/55 px-1.5 py-0.5 font-mono text-[10px] text-white">
              {duration}
            </span>
          )
        )}
      </button>
    </div>
  );
};

export const Route = createFileRoute("/library")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { folder?: string } => ({
    folder: typeof search.folder === "string" ? search.folder : undefined,
  }),
  loader: ({ context }) => requireAuth(context.queryClient),
  component: LibraryPage,
});
